'use client';

// Wraps next/link with explicit prefetch policy + bot detection. This is the
// single chokepoint for app-internal links — direct imports of next/link are
// forbidden by the Biome rule outside this directory.
//
// Prefetch is OFF by default. Each call site opts in via `prefetch="..."`:
//   'none'     — never prefetch (default; safe for footer, link clouds, etc.)
//   'hover'    — only on pointer hover or keyboard focus
//   'viewport' — when the link scrolls within rootMargin of the viewport
//   'instant'  — immediately on mount, bypassing observer/intent
//
// All modes respect bot detection — bots/crawlers never trigger prefetch
// regardless of mode, so `?_rsc=<hash>` URLs are not emitted into their
// crawl queue.
//
// Non-prefetchable hrefs are short-circuited to a plain <a> tag — they bypass
// next/link and useLinkPrefetch entirely. This guards against accidental
// prefetch attempts on URLs that don't represent internal Next.js routes:
//   - hash anchors (#section) — same-page scroll, handled by useSmoothAnchorScroll
//   - protocol handlers (mailto:, tel:, sms:) — not navigable routes
//   - absolute URLs (http://, https://) — treated as external; internal links
//     in this codebase always use relative paths

import Link from 'next/link';
import { forwardRef, type MutableRefObject, type Ref, type RefCallback } from 'react';
import { type PrefetchMode, useLinkPrefetch } from '@//hooks/useLinkPrefetch';

const isNonPrefetchable = (href: string): boolean => {
  if (!href) return true;
  if (href.startsWith('#')) return true;
  if (/^(mailto|tel|sms):/i.test(href)) return true;
  if (/^https?:\/\//i.test(href)) return true;
  return false;
};

// Compose two event handlers safely
const composeHandlers = <T extends (...args: any[]) => void>(
  original?: T,
  additional?: T,
): T | undefined => {
  if (!original && !additional) return undefined;
  if (!original) return additional;
  if (!additional) return original;

  return ((...args: Parameters<T>) => {
    original(...args);
    additional(...args);
  }) as T;
};

// Compose two refs safely
const composeRefs = <T,>(...refs: (Ref<T> | undefined)[]): RefCallback<T> | null => {
  const validRefs = refs.filter(Boolean) as Ref<T>[];
  if (validRefs.length === 0) return null;

  return (node: T | null) => {
    validRefs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        (ref as MutableRefObject<T | null>).current = node;
      }
    });
  };
};

interface AppLinkProps extends Omit<React.ComponentProps<typeof Link>, 'href' | 'prefetch'> {
  href: string;
  /** Prefetch policy. Default 'none'. See module comment for modes. */
  prefetch?: PrefetchMode;
}

export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(
  (
    {
      href,
      prefetch = 'none',
      children,
      onPointerEnter,
      onPointerLeave,
      onFocus,
      replace,
      scroll,
      locale,
      ...rest
    },
    ref,
  ) => {
    const nonPrefetchable = isNonPrefetchable(href);
    // Hook is called unconditionally to satisfy rules-of-hooks. When the href
    // is non-prefetchable we force mode='none' so the hook sets up no
    // observers, handlers, or state transitions.
    const { shouldPrefetch, ...prefetchBind } = useLinkPrefetch({
      mode: nonPrefetchable ? 'none' : prefetch,
    });

    if (nonPrefetchable) {
      // Plain <a> — bypass next/link entirely. `replace`, `scroll`, `locale`
      // are intentionally dropped (next/link-specific, meaningless for these
      // hrefs).
      return (
        <a
          href={href}
          ref={ref}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onFocus={onFocus}
          {...rest}
        >
          {children}
        </a>
      );
    }

    return (
      <Link
        href={href}
        prefetch={shouldPrefetch}
        replace={replace}
        scroll={scroll}
        locale={locale}
        {...rest}
        ref={composeRefs(ref, prefetchBind.ref)}
        onPointerEnter={composeHandlers(onPointerEnter, prefetchBind.onPointerEnter)}
        onPointerLeave={composeHandlers(onPointerLeave, prefetchBind.onPointerLeave)}
        onFocus={composeHandlers(onFocus, prefetchBind.onFocus)}
      >
        {children}
      </Link>
    );
  },
);

AppLink.displayName = 'AppLink';
